<template>
    <page-container maxSize="2xl">
        <analytics-franchise-hub-page-header
            :backButtonHref="`/analytics/franchise-hub/${props.category}`"
            backButtonText="Back to Category"
            :fetchParams="debouncedFetchParams"
            :category="props.category"
            :metric="props.metric"
        />
        <div class="flex flex-col gap-6 xl:flex-row">
            <div class="shrink-0 xl:w-64">
                <analytics-sidebar
                    v-model="fetchParams"
                    hideCategoryFilter
                    hideResourceFilter
                    hideLockedSnippetsFilter
                    @reset="resetFilters()"
                    :minAllowedDate="minAllowedDate"
                    class="transition"
                    :class="dotsSelected ? 'opacity-5' : 'duration-200 ease-in'"
                ></analytics-sidebar>
            </div>

            <div class="w-full relative">
                <transition
                    enterFromClass="opacity-0 -translate-y-4"
                    enterToClass="opacity-100 translate-y-0"
                    leaveFromClass="opacity-100 translate-y-0"
                    leaveToClass="opacity-0 -translate-y-4"
                >
                    <div v-if="showLoadingBars" class="absolute w-full transition pt-20">
                        <loading-bars></loading-bars>
                    </div>
                </transition>

                <div
                    v-if="debouncedFetchParams"
                    class="flex flex-col gap-6"
                    :class="{
                        'opacity-0 pointer-events-none': showLoadingBars,
                        'opacity-100 delay-200 transition duration-150': !showLoadingBars,
                    }"
                >
                    <div v-if="props.category !== 'none'" class="flex flex-col gap-6">
                        <analytics-outbound-messages-category-metric-group-plot
                            ref="categoryMetricGroupPlotChart"
                            :fetchParams="debouncedFetchParams"
                            :metric="props.metric"
                            :category="props.category"
                            @selected="dotsSelected = true"
                            @deselected="dotsSelected = false"
                        />

                        <div
                            class="flex flex-col gap-4 xl:justify-between xl:items-center xl:flex-row transition"
                            :class="{
                                'opacity-5': dotsSelected,
                                'duration-200 ease-in': !dotsSelected,
                            }"
                        >
                            <span class="font-bold text-xl">Performance by Metric</span>
                            <analytics-performance-legend />
                        </div>

                        <div
                            class="grid gap-6 grid-cols-1 transition"
                            :class="dotsSelected ? 'opacity-5' : ' duration-200 ease-in'"
                        >
                            <category-metric-card
                                :fetchParams="debouncedFetchParams"
                                :metric="props.metric"
                                :category="props.category"
                                hideDetailsButton
                            />
                        </div>

                        <div
                            class="grid grid-cols-1 gap-6 lg:grid-cols-2"
                            :class="dotsSelected ? 'opacity-5' : ' duration-200 ease-in'"
                        >
                            <analytics-category-metric-messages-card
                                class="col-span-1"
                                :fetchParams="fetchParams"
                                type="best"
                                :metric="props.metric"
                                :category="props.category"
                            />
                            <analytics-category-metric-messages-card
                                class="col-span-1"
                                :fetchParams="fetchParams"
                                type="worst"
                                :metric="props.metric"
                                :category="props.category"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </page-container>
</template>

<script setup>
import PageContainer from '~/components/general/PageContainer.vue';
import { computed, ref } from 'vue';
import AnalyticsSidebar from './AnalyticsSidebar.vue';
import { useDebouncedFetchParams } from '~/components/analytics/useDebouncedFetchParams';
import { getDateRange, getMinAllowedDate, resourceTypesOptions, metricsWithGoals } from '~/utils/analytics';
import { audienceTypes } from '~/constants/message';
import { SENDER_TYPES } from '~/constants/senderTypes';
import { analyticsCategoriesValues } from '~/constants/categories';
import LoadingBars from '~/components/general/LoadingBars.vue';
import CategoryMetricCard from '~/components/analytics/partial/category-metrics/CategoryMetricCard.vue';
import AnalyticsPerformanceLegend from '~/components/analytics/partial/AnalyticsPerformanceLegend.vue';
import AnalyticsOutboundMessagesCategoryMetricGroupPlot from './charts/time-series/AnalyticsOutboundMessagesCategoryMetricGroupPlot.vue';
import AnalyticsCategoryMetricMessagesCard from './charts/time-series/AnalyticsCategoryMetricMessagesCard.vue';
import AnalyticsFranchiseHubPageHeader from '~/components/analytics/partial/AnalyticsFranchiseHubPageHeader.vue';

const props = defineProps({
    category: {
        type: String,
        required: true,
        validator: (category) => analyticsCategoriesValues.includes(category),
    },
    metric: {
        type: String,
        required: true,
        validator: (metric) => metricsWithGoals.includes(metric),
    },
});

const defaultFilters = () => {
    const last30Days = getDateRange('last_30_days');

    return {
        range: [last30Days.start_date, last30Days.end_date],
        filters: {
            sender_types: SENDER_TYPES.map((senderType) => senderType.value),
            audience_types: audienceTypes().map((audienceType) => audienceType.name),
            resource_types: resourceTypesOptions.map((resourceType) => resourceType.value),
        },
    };
};

const { fetchParams, loading, debouncedFetchParams, resetFilters } = useDebouncedFetchParams(defaultFilters());

const minAllowedDate = getMinAllowedDate(true);

const dotsSelected = ref(false);

const categoryMetricGroupPlotChart = ref();
const showLoadingBars = computed(() => loading.value || categoryMetricGroupPlotChart.value?.loading);
</script>
